import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { pipe } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { MmAlertMode, MmAlertService, MmAlertSize, MmAlertType } from '@metromarkets/components';

import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { MAGIC_LINK_EXPIRY_QUERY_PARAM } from '~shared/constants/links.constants';
import { UrlUtil } from '~shared/utils/url.util';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_MAGIC_LINK_AUTH } from '../constants/authorization.constant';
import { AuthorizationApplicationEvent, NotificationApplicationEvent } from '~shared/modules/application-event/models';
import { GaTrackingEventAction, GaTrackingEventLabel } from '~shared/modules/ga-tracking/constants';
import { NUMBER_1 } from '~shared/constants';
import { GATrackingService } from '~shared/modules/ga-tracking/services';
import {
  ApplicationAuthorizationActionType,
  MagicLinkAuthorizationLabel,
} from '~shared/modules/application-event/constants';

@Injectable({
  providedIn: 'root',
})
export class MagicLinkEffectsService {
  constructor(
    private readonly router: Router,
    private readonly activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private readonly applicationEventService: ApplicationEventService,
    private readonly alertService: MmAlertService,
    private readonly translateService: TranslateService,
    private readonly gATrackingService: GATrackingService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public magicLinkExpiryObserver() {
    return pipe(
      map(() => {
        if (isPlatformBrowser(this.platformId)) {
          const isExpiry = !!UrlUtil.getQueryParam(MAGIC_LINK_EXPIRY_QUERY_PARAM);
          if (isExpiry) {
            UrlUtil.removeQueryParameters([MAGIC_LINK_EXPIRY_QUERY_PARAM], this.router);
          }
          return isExpiry;
        }
        return false;
      }),
      filter(Boolean),
      switchMap(() => this.activeFeaturesConfigcatService.isFeatureEnabledAndReadyV2(FF_MAGIC_LINK_AUTH)),
      tap((isMagicAuthEnabled) => {
        if (isMagicAuthEnabled) {
          this.gATrackingService.isTrackingStarted$.subscribe({
            complete: () => {
              this.applicationEventService.publish(
                new AuthorizationApplicationEvent(
                  ApplicationAuthorizationActionType.MagicLinkEmailRedirect,
                  MagicLinkAuthorizationLabel.Expired
                )
              );
              this.applicationEventService.publish(
                new NotificationApplicationEvent(
                  GaTrackingEventAction.CartNotifications,
                  GaTrackingEventLabel.MagicLoginLinkExpired
                )
              );
            },
          });

          this.showMagicLinkExpiryAlert();
        }
      })
    );
  }

  private showMagicLinkExpiryAlert() {
    const title = 'REGULAR.MAGIC_LINK_EXPIRED.ALERT.TITLE';
    const message = 'REGULAR.MAGIC_LINK_EXPIRED.ALERT.MESSAGE';

    this.translateService
      .get([title, message])
      .pipe(take(NUMBER_1))
      .subscribe((translations) => {
        this.alertService.addAlert({
          type: MmAlertType.error,
          // TODO: Bring back the alert title once the alert design is fixed
          // title: translations[title],
          message: translations[message],
          mode: MmAlertMode.alert,
          size: MmAlertSize.large,
        });
      });
  }
}
